// CSSでアンカー位置を補正する前提：JSはヘッダー高をCSS変数に反映するだけ
// ※ CSS例：
// html { scroll-behavior: smooth; }
// [id] { scroll-margin-top: calc(var(--header-h, 80px) + var(--anchor-gap, 24px)); }
// （内部スクロールコンテナがある場合）
// [data-scroll-root] {
//   scroll-behavior: smooth;
//   scroll-padding-top: calc(var(--header-h, 80px) + var(--anchor-gap, 24px));
// }

/**
 * スクロールで表示切替（KVを超えたら active に）
 */
export function toggleElementsByPosition(header, mvSection, elementsToToggle) {
  const kvBottom = mvSection?.offsetHeight || 0;
  const scrolled = window.scrollY > kvBottom;

  elementsToToggle.forEach(({ element }) => {
    if (!element) return;
    element.classList.toggle("active", scrolled);
    element.classList.toggle("hidden", !scrolled);
  });

  if (header) header.classList.toggle("active", scrolled);
}

/**
 * ヘッダー表示切替（スクロール方向で）
 * - 下方向に一定以上スクロール → 非表示（is-hide）
 * - 上方向にスクロール → 表示（is-show）
 * - メガナビ展開中（is-mega-open）は常に表示固定
 */
let lastScroll = 0;
const MIN_DELTA = 8; // 最小移動量（小さい振れを無視）
const HIDE_START = 100; // この位置より下で下方向なら隠す

export function toggleHeaderByScrollDirection(header) {
  if (!header) return;
  const y = window.scrollY;

  // メガナビ展開中は固定表示
  if (header.classList.contains("is-mega-open")) {
    header.classList.add("is-show");
    header.classList.remove("is-hide");
    lastScroll = y; // 基準を更新（閉じた直後の誤判定防止）
    return;
  }

  const diff = y - lastScroll;
  if (Math.abs(diff) < MIN_DELTA) return; // 小さな揺れは無視

  if (diff > 0 && y > HIDE_START) {
    // 下方向：隠す
    header.classList.remove("is-show");
    header.classList.add("is-hide");
  } else if (diff < 0) {
    // 上方向：見せる
    header.classList.remove("is-hide");
    header.classList.add("is-show");
  }

  lastScroll = y;
}

/** スクロール基準を現在位置にリセット（メガナビ閉じた直後などで呼ぶ） */
export function resetHeaderScrollBaseline() {
  lastScroll = window.scrollY || 0;
}

/**
 * ヘッダーやメニュー関連の要素取得に応じた調整
 * （既存レイアウト都合で必要なら継続。不要なら削除可）
 */
export function adjustByHeaderHeight(header, mvSection, menuBg) {
  if (!header) return;
  const headerHeight = header.offsetHeight;

  if (mvSection) {
    // mvSection.style.paddingBlockStart = `${headerHeight}px`;
    mvSection.style.marginBlockStart = `${headerHeight}px`;
  }
  if (menuBg) {
    menuBg.style.paddingBlockStart = `${headerHeight * 2}px`;
    // menuBg.style.paddingBlockStart = `${headerHeight * 1.25}px`;
  }
}

/* ================================
   CSSベースのアンカー位置補正 初期化
   ================================ */

/**
 * ヘッダー高さと任意余白をCSS変数に反映
 * - --header-h    : ヘッダーの現在高さ
 * - --anchor-gap  : 任意の追加余白（px）
 * CSS側で scroll-padding-top / scroll-margin-top に利用してください
 */
function initAnchorOffsetVars(header, gap = 120) {
  const apply = () => {
    const h = header?.offsetHeight || 0;
    const g = Number.isFinite(gap) ? gap : 24;
    const root = document.documentElement;
    root.style.setProperty("--header-h", `${h}px`);
    root.style.setProperty("--anchor-gap", `${g}px`);
  };

  // 初期適用
  apply();

  // ヘッダーサイズ変化に追従（表示/非表示やレスポンシブで高さが変わる想定）
  if (window.ResizeObserver && header) {
    const ro = new ResizeObserver(() => apply());
    ro.observe(header);
  } else {
    // フォールバック：リサイズ時に更新
    window.addEventListener("resize", apply, { passive: true });
  }
}

/**
 * 互換用エクスポート：
 * 以前の enableSmoothAnchors(header, gap) 呼び出しをそのまま使えるよう、
 * スクロール処理は行わず「CSS変数の初期化だけ」行います。
 */
export function enableSmoothAnchors(header, gap = 120) {
  initAnchorOffsetVars(header, gap);
  // ※ スクロールはCSSに任せるため、JSでのscrollToは行いません
}

/* ======================================================
   「ページ外からの #」で初回表示がズレる対策（再スナップ）
   ====================================================== */
/**
 * 使い方：
 * enableSmoothAnchors(header, 120) の直後に resnapHashAfterInit() を一度呼ぶ。
 * - 初回ロード直後に 1フレーム後で再スナップ
 * - window.load 後にも一度だけ再スナップ（画像等でレイアウトが伸びた場合）
 * - 以降の hashchange にも対応
 */
export function resnapHashAfterInit() {
  const resnap = () => {
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ block: "start" });
  };

  if (location.hash) requestAnimationFrame(resnap);

  window.addEventListener(
    "load",
    () => {
      if (location.hash) resnap();
    },
    { once: true }
  );

  window.addEventListener("hashchange", () => {
    requestAnimationFrame(resnap);
  });
}
